import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import structuredClone from '@ungap/structured-clone';
import { Subscription } from 'rxjs';

import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { UserV2 } from 'src/app/parameters/users/shared/models';
import { AlcmeonCardFooter } from 'src/app/shared/components/card-footer/models/alcmeon-card-footer.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { MediasService } from 'src/app/shared/services/medias.service';
import { InMessageEmailCustomizationService } from './in-message-email-customization.service';
import { InMessageEmailCustomization } from './models';

@Component({
  selector: 'app-in-message-email-customization',
  templateUrl: './in-message-email-customization.component.html',
  styleUrls: ['./in-message-email-customization.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InMessageEmailCustomizationComponent implements OnInit {
  @Input() siteId: number;

  blueAlcmeon = '#17b3e8';
  defaultMail = 'no-reply@alcmeon.com';
  defaultName = 'Alcméon';
  footerContent: AlcmeonCardFooter;
  initialSavedCusto: InMessageEmailCustomization;
  isCreation = true;
  usersMap: Map<number, UserV2>;
  validators = Validators;

  imagesErrors = {
    header: '',
    footer: '',
  };

  restrictions = {
    name: {
      length: 30,
    },
    object: {
      length: 50,
    },
    header: {
      image: {
        format: ['image/png', 'image/jpeg', 'image/gif'],
        weight: 400,
        width: 500,
        height: 70,
      },
    },
    footer: {
      image: {
        format: ['image/png', 'image/jpeg', 'image/gif'],
        weight: 400,
        width: 120,
        height: 60,
      },
      text: {
        length: 300,
      },
    },
  };

  form: FormGroup = this.formBuilder.group({
    defaultSenderEmail: [this.defaultMail, [Validators.required, Validators.email]],
    defaultSenderName: [this.defaultName, Validators.required],
    defaultObject: [''],

    enableHeader: [false],
    header: this.formBuilder.group({
      image: [{ value: '', disabled: true }, Validators.required],
      lineColor: [{ value: '', disabled: true }, Validators.required],
    }),

    enableFooter: [false],
    footer: this.formBuilder.group({
      image: [{ value: '', disabled: true }],
      lineColor: [{ value: '', disabled: true }, Validators.required],
      text: [{ value: '', disabled: true }, Validators.required],
    }),
  });

  @ViewChild('headerFileInput') headerFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('logoFileInput') logoFileInput!: ElementRef<HTMLInputElement>;

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private formBuilder: FormBuilder,
    private inMessageEmailCustomizationService: InMessageEmailCustomizationService,
    private mediaService: MediasService
  ) {}

  ngOnInit(): void {
    this.usersMap = this.alcmeonContextService.getCompany().usersMap;

    this.inMessageEmailCustomizationService.getCustomization(this.siteId).subscribe(custo => {
      this.isCreation = !custo.createdBy;

      if (!this.isCreation) {
        this.initValues(custo);
        this.updateFooter(custo);
        this.initialSavedCusto = custo;
      }
    });

    this.subscriptions.add(
      this.form.get('enableHeader').valueChanges.subscribe(enableHeader => {
        if (enableHeader) {
          this.form.get('header').enable();

          // no default value in formBuilder because when patchValue() above, value can be null
          if (!this.form.get('header.lineColor').value) {
            this.form.get('header.lineColor').setValue(this.blueAlcmeon);
          }
        } else {
          this.form.get('header').disable();
        }
      })
    );

    this.subscriptions.add(
      this.form.get('enableFooter').valueChanges.subscribe(enableFooter => {
        if (enableFooter) {
          this.form.get('footer').enable();

          // no default value in formBuilder because when patchValue() above, value can be null
          if (!this.form.get('footer.lineColor').value) {
            this.form.get('footer.lineColor').setValue(this.blueAlcmeon);
          }
        } else {
          this.form.get('footer').disable();
        }
      })
    );
  }

  private initValues(custo: InMessageEmailCustomization) {
    this.form.patchValue(custo);
    this.form.get('enableHeader').setValue(custo.header.enabled);
    this.form.get('enableFooter').setValue(custo.footer.enabled);
  }

  private updateFooter(custo: InMessageEmailCustomization) {
    this.footerContent = {
      createdBy: this.usersMap.get(custo.createdBy)?.realname,
      createdAt: custo.createdAt,
      modifiedBy: this.usersMap.get(custo.updatedBy)?.realname,
      modifiedAt: custo.updatedAt,
    };
  }

  clickToLoadHeaderImage() {
    this.headerFileInput.nativeElement.value = ''; // allow selecting the same file again
    this.headerFileInput.nativeElement.click();
  }

  clickToLoadFooterImage() {
    this.logoFileInput.nativeElement.value = ''; // allow selecting the same file again
    this.logoFileInput.nativeElement.click();
  }

  loadImage(event: Event, whichImage: 'header' | 'footer') {
    this.imagesErrors[whichImage] = '';

    const file = (event.target as HTMLInputElement).files[0];

    if (file) {
      if (!this.restrictions[whichImage].image.format.includes(file.type)) {
        this.imagesErrors[whichImage] = this.basicService.translate(
          'accounts/edition/in-message/email-customization/error/img-format'
        );
        return;
      }

      if (file.size > this.restrictions[whichImage].image.weight * 1024) {
        this.imagesErrors[whichImage] = this.basicService.translate(
          'accounts/edition/in-message/email-customization/error/img-weight'
        );

        return;
      }

      this.mediaService.getImageInfo(file).subscribe({
        next: imageInfos => {
          if (
            imageInfos.width > this.restrictions[whichImage].image.width ||
            imageInfos.height > this.restrictions[whichImage].image.height
          ) {
            this.imagesErrors[whichImage] = this.basicService.translate(
              'accounts/edition/in-message/email-customization/error/img-dimensions'
            );

            return;
          }

          this.form.get(`${whichImage}.image`).setValue(imageInfos.b64Url);
          this.form.get(`${whichImage}.image`).markAsDirty();
        },
        error: () => {
          this.imagesErrors[whichImage] = this.basicService.translate(
            'accounts/edition/in-message/email-customization/error/img-corrupted'
          );
        },
      });
    }
  }

  removeImage(whichImage: 'header' | 'footer') {
    this.imagesErrors[whichImage] = '';
    this.form.get(`${whichImage}.image`).setValue('');
    this.form.get(`${whichImage}.image`).markAsDirty();
  }

  save() {
    const savedForm: InMessageEmailCustomization = structuredClone(this.form.value);

    if (savedForm.enableHeader) {
      savedForm.header.enabled = true;
    } else {
      savedForm.header = { enabled: false };
    }
    delete savedForm.enableHeader;

    if (savedForm.enableFooter) {
      savedForm.footer.enabled = true;
    } else {
      savedForm.footer = { enabled: false };
    }
    delete savedForm.enableFooter;

    const action$ = this.isCreation
      ? this.inMessageEmailCustomizationService.createCustomization(this.siteId, savedForm)
      : this.inMessageEmailCustomizationService.updateCustomization(this.siteId, savedForm);

    action$.subscribe(custo => {
      this.basicService.toastSuccess('accounts/edition/in-message/email-customization/button/publish-success');
      this.initialSavedCusto = custo;
      this.initValues(custo);
      this.updateFooter(custo);
      this.isCreation = false;
      this.form.markAsPristine();
    });
  }

  cancel() {
    if (this.isCreation) {
      this.form.reset();
      this.form.get('defaultSenderEmail').setValue(this.defaultMail);
      this.form.get('defaultSenderName').setValue(this.defaultName);
    } else {
      this.initValues(this.initialSavedCusto);
    }

    this.imagesErrors.header = '';
    this.imagesErrors.footer = '';
    this.form.markAsPristine();
  }
}
